import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useModal } from '@components/Modal/ModalProvider';
import { usePanelContainer } from '@features/analysis/rendering/Panels/usePanelContainer';
import {
  setConstantPose,
  SurfaceContext,
  updateResetCamera,
  useBufferPool,
} from '@features/analysis/rendering/Panels/Renderers/vtkManagers';
import { useBrowserPage } from '@features/browser/useBrowserPage';
import { Series } from '@models/Series';
import vtkActor from '@kitware/vtk.js/Rendering/Core/Actor';

import ReportPreViewModel, { imageSet, TItem } from './FFRReportPreViewModel';
import {
  captureView,
  composeBaseImageWithSvgOverlay,
  cropToSquare,
  screenshotElement,
} from 'utils/captureUtils';
import { setAxesLinesVisibility } from '@features/view/axesLines';
import { setActorOpacity, setActorVisible } from '@features/view/Shared3DUtilities';
import { FFR_OPACITY, FFRNames, nodeNames } from 'config/render3DConfig';
import { configStore } from 'config/configStore';
import { AnalysisType } from 'config/analysisConfig';
import BaseModalContent from '../BaseReportModalContent/BaseReportModalContetnt';
import printIconUrl from 'img/print-solid.svg';
import './FFRReportPreViewModel.scss';
import { useReactToPrint } from 'react-to-print';

export const LOCAL_STORAGE_KEY = {
  RESULT_CHART_TYPE: 'chartType',
  REPORT_LANGUAGE_KEY: 'healthCheckupReportLanguage',
  TOKEN_EXP: 'exp',
  TABLE_HEADER_SETTING: 'headerSetting',
};

const DEFAULT_LANGUAGE_OPTIONS: TItem[] = [
  { id: 'ko', name: 'Korean', isSelected: true },
  { id: 'en', name: 'English', isSelected: false },
  { id: 'ru', name: 'Russian', isSelected: false },
  { id: 'zhCN', name: 'Chinese (Simplified)', isSelected: false },
  { id: 'zhTW', name: 'Chinese (Traditional)', isSelected: false },
  { id: 'ja', name: 'Japanese', isSelected: false },
];

const CAPTURE_POSES = ['A', 'P', 'L', 'R'] as const;

function waitForNextPaint(frames = 1): Promise<void> {
  return new Promise((resolve) => {
    const tick = (count: number) => {
      requestAnimationFrame(() => {
        count <= 1 ? resolve() : tick(count - 1);
      });
    };

    tick(Math.max(1, frames));
  });
}

function getSavedLanguageOptions() {
  const savedLanguage = localStorage.getItem(
    LOCAL_STORAGE_KEY.REPORT_LANGUAGE_KEY,
  );

  if (!savedLanguage) return DEFAULT_LANGUAGE_OPTIONS;

  return DEFAULT_LANGUAGE_OPTIONS.map((item) => ({
    ...item,
    isSelected: item.id === savedLanguage,
  }));
}

export function ReportDescription({
  series,
  ct_ffr,
  imageUrls,
  reportRef,
}: {
  series: Series;
  ct_ffr: any;
  imageUrls: imageSet | undefined;
  reportRef: React.RefObject<HTMLDivElement>;
}) {
  const [languageOptions, setLanguageOptions] = useState<TItem[]>(
    getSavedLanguageOptions,
  );

  const selectedLanguage = useMemo(
    () => languageOptions.find((item) => item.isSelected)?.id ?? 'ko',
    [languageOptions],
  );

  const onLanguageChange = useCallback((updatedItems: TItem[]) => {
    const selectedItem = updatedItems.find((item) => item.isSelected);
    if (!selectedItem) return;

    setLanguageOptions(updatedItems);
    localStorage.setItem(
      LOCAL_STORAGE_KEY.REPORT_LANGUAGE_KEY,
      String(selectedItem.id),
    );
  }, []);

  return (
    <ReportPreViewModel
      series={series}
      ct_ffr={ct_ffr}
      images={imageUrls}
      language={selectedLanguage}
      languageOptions={languageOptions}
      onLanguageChange={onLanguageChange}
      reportRef={reportRef}
    />
  );
}

async function replaceDarkBackgroundToWhite(dataUrl: string): Promise<string> {
  const img = new Image();
  img.src = dataUrl;
  await img.decode();

  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth || img.width;
  canvas.height = img.naturalHeight || img.height;

  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(img, 0, 0);

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;

  const T = 35;

  for (let i = 0; i < data.length; i += 4) {
    if (data[i] < T && data[i + 1] < T && data[i + 2] < T) {
      data[i] = 255;
      data[i + 1] = 255;
      data[i + 2] = 255;
      data[i + 3] = 255;
    }
  }

  ctx.putImageData(imageData, 0, 0);
  return canvas.toDataURL('image/png');
}

async function captureOneImage(
  context: SurfaceContext,
): Promise<string | undefined> {
  const { genericRenderWindow, renderWindow } = context;
  const container = genericRenderWindow.getContainer() as HTMLDivElement | null;

  await waitForNextPaint(2);

  const vtkImage = await captureView({
    type: 'vtk',
    renderWindow,
  });

      const whiteVtkImage = await replaceDarkBackgroundToWhite(vtkImage);


  return container
      ? await composeBaseImageWithSvgOverlay(whiteVtkImage, container)
      : whiteVtkImage;


  // if (container && window.capture?.region) {
  //   try {
  //     return await screenshotElement(container);
  //   } catch (error) {
  //     console.warn('[captureSurface3D] Native/DOM capture failed.', error);
  //   }
  // }
  // try {
  //   const vtkImage = await captureView({
  //     type: 'vtk',
  //     renderWindow,
  //   });

  //   return container
  //     ? await composeBaseImageWithSvgOverlay(vtkImage, container)
  //     : vtkImage;
  // } catch (error) {
  //   console.warn('[captureSurface3D] VTK capture failed.', error);
  // }

  // if (!container) return undefined;

  // try {
  //   return await screenshotElement(container);
  // } catch (error) {
  //   console.warn('[captureSurface3D] DOM fallback capture failed.', error);
  //   return undefined;
  // }
}


export async function captureDirections(
  context: React.RefObject<SurfaceContext | null>,
  analysisType: AnalysisType,
): Promise<string[] | undefined> {
  if (!context.current) return;

  const surfaceContext = context.current;
  const { renderer, renderWindow, orientationWidget } = surfaceContext;
  const camera = renderer.getActiveCamera();
  const originalCamera = {
    position: camera.getPosition(),
    viewUp: camera.getViewUp(),
    focalPoint: camera.getFocalPoint(),
    parallelScale: camera.getParallelScale(),
  };

  orientationWidget?.setEnabled(false);

  try {
    const results: string[] = [];

    for (const pose of CAPTURE_POSES) {
      setConstantPose(pose, camera);

      renderer.resetCameraClippingRange();
      renderWindow.render();

      const url = await captureOneImage(surfaceContext);
      const image =  url ? await cropToSquare(url) : undefined;
      if (image) results.push(image);
    }

    return results;
  } finally {
    updateResetCamera(context, analysisType);

    camera.setPosition(...originalCamera.position);
    camera.setFocalPoint(...originalCamera.focalPoint);
    camera.setViewUp(originalCamera.viewUp);

    if (analysisType === 'FFR') {
      camera.setParallelScale(originalCamera.parallelScale);
    }

    renderer.resetCameraClippingRange();
    orientationWidget?.setEnabled(true);
    renderWindow.render();
  }
}

export async function capture(
  context: React.MutableRefObject<SurfaceContext | null>,
  analysisType: AnalysisType,
): Promise<string[] | undefined> {
  if (!context.current) return;

  const {
    actorIds,
    orientationWidget,
    genericRenderWindow,
    renderWindow,
    renderer,
  } = context.current;

  const oldBackground = renderer.getBackground() as [number, number, number];

  const FFR_TARGETS = [
    FFRNames.main_lca,
    FFRNames.main_rca,
    FFRNames.cap_lca,
    FFRNames.cap_rca,
    'aorta',
  ] as const;

  const CENTERLINE_TARGET = nodeNames.singleCenterLine;

  const captureActorIds = [...FFR_TARGETS, CENTERLINE_TARGET];

  const actorStates = captureActorIds.map((actorId) => {
    const actor = actorIds[actorId] as vtkActor | undefined;

    return {
      actorId,
      visible: actor?.getVisibility() ?? false,
      opacity: actor?.getProperty().getOpacity() ?? 0,
    };
  });

  try {
    actorIds[CENTERLINE_TARGET]?.setVisibility(false);

    setActorVisible(context, true, [...FFR_TARGETS]);
    setActorOpacity(context, [...FFR_TARGETS], FFR_OPACITY.FULL);

    setAxesLinesVisibility({ context, visible: false });
    orientationWidget?.setEnabled(false);

    renderWindow.render();

    return await captureDirections(context, analysisType);
  } finally {
    renderer.setBackground(...oldBackground);
    orientationWidget?.setEnabled(true);

    setAxesLinesVisibility({
      context,
      visible: context.current?.isAxis3DVisible ?? false,
    });

    actorStates.forEach(({ actorId, visible, opacity }) => {
      const actor = actorIds[actorId] as vtkActor | undefined;
      if (!actor) return;

      actor.setVisibility(visible);
      actor.getProperty().setOpacity(opacity);
    });

    genericRenderWindow.resize();
    renderWindow.render();
  }
}

function toImageSet(urls: string[]): imageSet {
  const [one, two, three, four] = urls;
  return { one, two, three, four };
}

export default function useFFRPreviewModal() {
  const { openModal, closeModal } = useModal();

  const { selectedSeries, selectedSeriesId } = useBrowserPage();
  const { contextPool, bufferPool } = useBufferPool();
  const { analysisType } = usePanelContainer();

  const modalIdRef = useRef<string | null>(null);
  const poolRef = useRef(contextPool);
  const reportRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    contentRef: reportRef,
    documentTitle: 'CT-FFR_Report',
  });

  useEffect(() => {
    poolRef.current = contextPool;
  }, [contextPool]);

  const closeWithFlagOff = useCallback(() => {
    if (!modalIdRef.current) return;
    closeModal(modalIdRef.current);
    modalIdRef.current = null;
  }, [closeModal]);

  const capture3d = useCallback(async (): Promise<imageSet | undefined> => {
    if (!selectedSeriesId) return;
    const surfaceContext = poolRef.current['Surface3D'] as
      | SurfaceContext
      | undefined;

    if (!surfaceContext) return;

    const capturedUrls = await capture(
      { current: surfaceContext },
      analysisType,
    );

    return capturedUrls?.length ? toImageSet(capturedUrls) : undefined;
  }, [analysisType, selectedSeriesId]);

  const captureWithFlag = useCallback(async () => {
    configStore.setData({ isCapture: true, isSvgCapture:true });
    try {
      return await capture3d();
    } finally {
      configStore.setData({ isCapture: false, isSvgCapture:false });
    }
  }, [capture3d]);

  const handleOpenPrintPreview = useCallback(async () => {
    if (!selectedSeries || !selectedSeriesId || modalIdRef.current) return;

    const pool = bufferPool[selectedSeriesId];
    const imageUrls = await captureWithFlag();

    modalIdRef.current = openModal(
      <BaseModalContent
        className="reportPreview"
        title="FFR Report Preview"
        capturebutton={false}
        headerActions={
          <button
            type="button"
            className="print-button"
            data-capture-exclude="true"
            title="Print"
            aria-label="Print plaque report"
            onClick={handlePrint}
          >
            <img src={printIconUrl} alt="" draggable={false} />
          </button>
        }
        onClose={closeWithFlagOff}
      >
        <ReportDescription
          series={selectedSeries}
          ct_ffr={pool?.ct_ffr?.data}
          imageUrls={imageUrls}
          reportRef={reportRef}
        />
      </BaseModalContent>,
      false,
      closeWithFlagOff,
    );
  }, [
    selectedSeries,
    selectedSeriesId,
    bufferPool,
    captureWithFlag,
    openModal,
    closeWithFlagOff,
    handlePrint,
  ]);

  useEffect(() => {
    return closeWithFlagOff;
  }, [closeWithFlagOff]);

  return handleOpenPrintPreview;
}
